import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import DateInput from '../components/DateInput';

const API_URL = process.env.REACT_APP_API_URL || '';

interface PreviousInsurance {
  id?: string;
  description: string;
  policyNumber: string;
  insurer: string;
  startDate: string;
  endDate: string;
}

const AddPreviousInsurancePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddPreviousInsurancePage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode 
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<PreviousInsurance>({
    description: '',
    policyNumber: '',
    insurer: '',
    startDate: '',
    endDate: ''
  });
  
  const [errors, setErrors] = useState({
    description: '',
    policyNumber: '',
    insurer: '',
    startDate: '',
    endDate: '',
    server: ''
  });

  // ✅ NEW: Track successful draft save so we can show a green banner
  const [draftSaved, setDraftSaved] = useState(false);

  // Fetch existing insurance if editing
  useEffect(() => {
    const editId = new URLSearchParams(location.search).get('editId');
    if (editId && quoteId) {
      setEditingId(editId);
      fetchInsuranceForEdit(editId);
    }
  }, [quoteId, location.search]);

  const fetchInsuranceForEdit = async (id: string) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/previous-insurances/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const insurance = response.data.data || response.data;
    
    // Helper function to format date for input field (YYYY-MM-DD)
    const formatDateForInput = (dateString: string) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        // Format as YYYY-MM-DD for input[type="date"]
        return date.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };
    
    console.log('📅 Raw dates from API:', { 
      start: insurance.start_date || insurance.startDate,
      end: insurance.end_date || insurance.endDate
    });
    console.log('📅 Formatted dates for date picker:', {
      start: formatDateForInput(insurance.start_date || insurance.startDate),
      end: formatDateForInput(insurance.end_date || insurance.endDate)
    });
    
    setFormData({
      description: insurance.description || '',
      policyNumber: insurance.policy_number || insurance.policyNumber || '',
      insurer: insurance.insurer || '',
      startDate: formatDateForInput(insurance.start_date || insurance.startDate),
      endDate: formatDateForInput(insurance.end_date || insurance.endDate)
    });
  } catch (error) {
    console.error('Error fetching insurance for edit:', error);
  }
};

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '', server: '' }));
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = { ...errors };
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }
    if (!formData.policyNumber.trim()) {
      newErrors.policyNumber = 'Policy Number is required';
      isValid = false;
    }
    if (!formData.insurer.trim()) {
      newErrors.insurer = 'Insurer is required';
      isValid = false;
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start Date is required';
      isValid = false;
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End Date is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

          const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!validateForm()) return;
  
  // Validate clientId
  if (!clientId || isNaN(parseInt(clientId))) {
    setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
    return;
  }
  
  setIsLoading(true);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    // Clean the quoteId - convert "null" string or invalid values to actual null
let cleanQuoteId: number | null = null;
if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
  cleanQuoteId = parseInt(quoteId);
}
    
    // Helper function to format date for backend (YYYY-MM-DD)
const formatDateForBackend = (dateString: string) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

const insuranceData = {
  description: formData.description,
  policy_number: formData.policyNumber,
  insurer: formData.insurer,
  start_date: formatDateForBackend(formData.startDate),
  end_date: formatDateForBackend(formData.endDate),
  quote_id: cleanQuoteId,
  client_id: parseInt(clientId)
};

console.log('📅 Saving dates in YYYY-MM-DD format:', {
  start_date: formatDateForBackend(formData.startDate),
  end_date: formatDateForBackend(formData.endDate)
});
    
    // If no quote_id, save to localStorage (draft mode)
    if (!cleanQuoteId) {
      console.log('💾 No quote ID - saving to localStorage as draft');
      
      // Get existing draft previous insurances from localStorage
      const storageKey = `client_${clientId}_previous_insurances_draft`;
      const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingDrafts.push({
        ...insuranceData,
        id: `temp-${Date.now()}`,
        isTemp: true
      });
              localStorage.setItem(storageKey, JSON.stringify(existingDrafts));
        
        // ✅ Use a dedicated success flag, not errors.server
        setDraftSaved(true);
        setErrors(prev => ({ ...prev, server: '' }));

      setTimeout(() => {
        if (isMasterQuoteMode && returnTo) {
    // Should not happen for draft mode, but handle gracefully
    const decodedReturnUrl = decodeURIComponent(returnTo);
    navigate(decodedReturnUrl);
  } else {
    navigate(`/create-quote/${clientId}`, { 
      state: { activeTab: 'quotes', refreshProducts: true, hasDrafts: true }
    });
  }
}, 1500);
    } else {
  // Has quote_id - save to database
  if (editingId) {
    await axios.put(
      `${API_URL}/api/previous-insurances/${editingId}`,
      insuranceData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } else {
    await axios.post(
      `${API_URL}/api/previous-insurances`,
      insuranceData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }
  
  // ✅ FIX: Navigate based on mode
  setTimeout(() => {
    const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
    
    if (!validClientId) {
      console.error('❌ No clientId available for navigation');
      setErrors(prev => ({ ...prev, server: 'Error: Missing client ID' }));
      setIsLoading(false);
      return;
    }
    
    if (isMasterQuoteMode && returnTo) {
      // Master quote mode - navigate back to master quote page
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=previous_insurance&clientId=${validClientId}`;
      console.log('📍 Returning to master quote page:', finalUrl);
      navigate(finalUrl);
    } else if (cleanQuoteId) {
      // Create quote mode but with quoteId - navigate to create quote page
      navigate(`/create-quote/${validClientId}`, { 
        state: { refreshProducts: true, activeTab: 'quotes' } 
      });
    } else {
      // Fallback
      navigate(`/create-quote/${validClientId}`, { 
        state: { refreshProducts: true, activeTab: 'quotes' } 
      });
    }
  }, 1500);
}
    
  } catch (error: any) {
    setErrors(prev => ({
      ...prev,
      server: error.response?.data?.message || `Failed to ${editingId ? 'update' : 'add'} previous insurance`
    }));
  } finally {
    setIsLoading(false);
  }
};

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Previous Insurance' : 'Add Previous Insurance'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">

            <button
              onClick={() => {
                sessionStorage.setItem(`returning_from_product_${clientId}`, 'true');
                if (isMasterQuoteMode && returnTo) {
                  // Go back to master quote page with refresh flag
                  const decodedReturnUrl = decodeURIComponent(returnTo);
                  const separator = decodedReturnUrl.includes('?') ? '&' : '?';
                  const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=previous_insurance&clientId=${clientId}`;
                  console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
                  navigate(finalUrl);
                } else {
                  // Go back to create quote page
                  navigate(`/create-quote/${clientId}`, { state: { fromCancel: true } });
                }
              }}
              className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              ← Back to Quote
            </button>
            
            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                {editingId ? 'Edit Previous Insurance' : 'Add Previous Insurance'}
              </h3>
              
              {draftSaved && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                  ✅ Previous insurance added to draft — it will be saved when you create the quote.
                </div>
              )}

              {errors.server && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errors.server}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Description */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-3 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="e.g. Comprehensive motor vehicle insurance with Old Mutual"
                  />
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>
                
                {/* Policy Number and Insurer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Policy Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="policyNumber"
                      value={formData.policyNumber}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border ${errors.policyNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder="e.g. OMV123456789"
                    />
                    {errors.policyNumber && <p className="text-red-500 text-sm mt-1">{errors.policyNumber}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Insurer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="insurer"
                      value={formData.insurer}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border ${errors.insurer ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder="e.g. Old Mutual, Santam, Hollard"
                    />
                    {errors.insurer && <p className="text-red-500 text-sm mt-1">{errors.insurer}</p>}
                  </div>
                </div>
                
                {/* Start Date and End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      value={formData.startDate}
                      onChange={(date) => {
                        setFormData(prev => ({ ...prev, startDate: date }));
                        setErrors(prev => ({ ...prev, startDate: '' }));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      value={formData.endDate}
                      onChange={(date) => {
                        setFormData(prev => ({ ...prev, endDate: date }));
                        setErrors(prev => ({ ...prev, endDate: '' }));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t">

                  <button
                    type="button"
                    onClick={() => {
                      // ✅ Set flag so CreateQuotePage knows not to reset
                      sessionStorage.setItem(`returning_from_product_${clientId}`, 'true');
                      
                      if (isMasterQuoteMode && returnTo) {
                        // Go back to master quote page with refresh flag
                        const decodedReturnUrl = decodeURIComponent(returnTo);
                        const separator = decodedReturnUrl.includes('?') ? '&' : '?';
                        const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=previous_insurance&clientId=${clientId}`;
                        console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
                        navigate(finalUrl);
                      } else {
                        // ✅ Pass fromCancel state so CreateQuotePage skips the reset
                        navigate(`/create-quote/${clientId}`, { state: { fromCancel: true } });
                      }
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-md disabled:bg-gray-400"
                  >
                    {isLoading ? 'Saving...' : (editingId ? 'Update Insurance' : 'Add Insurance')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPreviousInsurancePage;